import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, FileSignature, Building2, User, MapPin, Phone, Mail,
  Calendar, Globe, Shield, Plus, Trash2, Save, ChevronRight,
  ChevronLeft, Check, AlertCircle, RefreshCw, Loader2, Search
} from 'lucide-react';
import { toast } from '../../../utils/toastUtils';
import { contracts as contractsApi, workspaces as workspacesApi } from '../../../services/api/client';
import api from '../../../services/api/client';

// InputField component defined OUTSIDE to prevent re-renders and focus loss
const InputField = ({ label, name, value, onChange, error, helperText, type = 'text', required, placeholder, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-300 mb-1">
      {label}{required && <>&nbsp;<span className="text-red-400">*</span></>}
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 bg-brand-black border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green ${
        error ? 'border-red-500' : 'border-gray-600'
      }`}
    />
    {error && (
      <p className="mt-1 text-xs text-red-400">{error}</p>
    )}
    {!error && helperText && (
      <p className="mt-1 text-xs text-gray-500">{helperText}</p>
    )}
  </div>
);

const CreateContractDialog = ({ workspace, templates, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [errors, setErrors] = useState({});
  const [searchingSiren, setSearchingSiren] = useState(false);
  const [sirenSearch, setSirenSearch] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Contract info
    template_id: '',
    title: `Contrat d'autorisation de test d'intrusion - ${workspace?.name || ''}`,

    // Provider info (pre-filled from company settings)
    provider_name: 'SYLink Security',
    provider_legal_form: 'SAS',
    provider_capital: '10 000 €',
    provider_address: '',
    provider_city: '',
    provider_postal_code: '',
    provider_country: 'France',
    provider_siren: '',
    provider_rcs: '',
    provider_representative_name: '',
    provider_representative_function: '',
    provider_contact_name: '',
    provider_contact_function: 'Pentester',
    provider_contact_email: '',
    provider_contact_phone: '',

    // Client info (pre-filled from workspace)
    client_name: workspace?.company_name || '',
    client_legal_form: workspace?.legal_form || '',
    client_capital: workspace?.capital || '',
    client_address: workspace?.address || '',
    client_city: workspace?.city || '',
    client_postal_code: workspace?.postal_code || '',
    client_country: workspace?.country || 'France',
    client_siret: workspace?.siret || '',
    client_rcs: '',
    client_representative_name: workspace?.contact_name || '',
    client_representative_function: workspace?.contact_position || '',
    client_contact_name: workspace?.contact_name || '',
    client_contact_email: workspace?.contact_email || '',
    client_contact_phone: workspace?.contact_phone || '',

    // Test period
    start_date: '',
    end_date: '',
    time_slots: 'Du lundi au vendredi, de 9h00 à 18h00',
    risk_period_days: 30,

    // Perimeter
    perimeter: {
      ip_ranges: [],
      domains: [],
      applications: [],
      exclusions: [],
      test_types: ['external', 'internal'],
      special_conditions: ''
    },

    // Legal
    deliverables_description: 'Rapport de test d\'intrusion détaillé incluant les vulnérabilités découvertes, leur criticité, et les recommandations de remédiation.',
    applicable_law: 'Droit français',
    jurisdiction: 'Tribunaux de Paris'
  });

  // Perimeter input states
  const [newIpRange, setNewIpRange] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newApp, setNewApp] = useState({ name: '', url: '', description: '' });
  const [newExclusion, setNewExclusion] = useState('');

  // Load provider profile from account settings
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await api.get('/account/profile');
        // Handle both response.data and response.data.data patterns
        const profile = response.data?.data || response.data;

        if (profile) {
          const company = profile.company || {};

          // Build user full name from first_name + last_name, fallback to full_name or username
          const userFullName = (profile.first_name && profile.last_name)
            ? `${profile.first_name} ${profile.last_name}`
            : profile.full_name || profile.username || '';

          // Contact technique = nom de l'utilisateur connecté
          const contactName = userFullName;
          const contactFunction = profile.job_title || 'Pentester';
          const contactEmail = profile.email || '';
          const contactPhone = profile.phone || company.contact_phone || '';

          // Représentant légal = contact_name de la société ou même que contact technique
          const representativeName = company.contact_name || userFullName;
          const representativeFunction = contactFunction;

          setFormData(prev => ({
            ...prev,
            // Provider info from company
            provider_name: company.name || 'SYLink Security',
            provider_legal_form: company.legal_form || 'SAS',
            provider_capital: company.capital || '10 000 €',
            provider_address: company.address || '',
            provider_city: company.city || '',
            provider_postal_code: company.postal_code || '',
            provider_country: company.country || 'France',
            provider_siren: company.siren || '',
            provider_rcs: company.rcs || '',
            // Contact technique = utilisateur connecté
            provider_contact_name: contactName,
            provider_contact_function: contactFunction,
            provider_contact_email: contactEmail,
            provider_contact_phone: contactPhone,
            // Représentant légal = par défaut même que contact technique
            provider_representative_name: representativeName,
            provider_representative_function: representativeFunction,
          }));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        // Continue with defaults if profile loading fails
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfile();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // SIREN/SIRET lookup for client info
  const handleSirenLookup = async () => {
    const searchValue = sirenSearch.trim() || formData.client_siret?.trim() || formData.client_name?.trim();
    if (!searchValue) {
      toast.error('Entrez un SIREN, SIRET ou nom de societe');
      return;
    }

    setSearchingSiren(true);
    try {
      let response;
      // Check if it's a SIREN (9 digits) or SIRET (14 digits)
      if (/^\d{9}$/.test(searchValue)) {
        response = await workspacesApi.lookupSiren(searchValue);
      } else if (/^\d{14}$/.test(searchValue)) {
        response = await workspacesApi.lookupSiret(searchValue);
      } else {
        // Search by name
        const results = await workspacesApi.searchPappers(searchValue);
        if (results && results.length > 0) {
          response = results[0];
        }
      }

      if (response) {
        setFormData(prev => ({
          ...prev,
          client_name: response.name || response.company_name || prev.client_name,
          client_legal_form: response.legal_form || prev.client_legal_form,
          client_capital: response.capital || prev.client_capital,
          client_address: response.address || prev.client_address,
          client_city: response.city || prev.client_city,
          client_postal_code: response.postal_code || prev.client_postal_code,
          client_country: response.country || prev.client_country || 'France',
          client_siret: response.siret || prev.client_siret,
          client_representative_name: response.representative_name || response.contact_name || prev.client_representative_name,
          client_representative_function: response.representative_function || 'Gerant',
        }));
        toast.success('Informations recuperees avec succes');
      } else {
        toast.error('Aucune entreprise trouvee');
      }
    } catch (error) {
      console.error('SIREN lookup error:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setSearchingSiren(false);
    }
  };

  // Perimeter handlers
  const addIpRange = () => {
    if (newIpRange && !formData.perimeter.ip_ranges.includes(newIpRange)) {
      setFormData(prev => ({
        ...prev,
        perimeter: {
          ...prev.perimeter,
          ip_ranges: [...prev.perimeter.ip_ranges, newIpRange]
        }
      }));
      setNewIpRange('');
    }
  };

  const removeIpRange = (ip) => {
    setFormData(prev => ({
      ...prev,
      perimeter: {
        ...prev.perimeter,
        ip_ranges: prev.perimeter.ip_ranges.filter(i => i !== ip)
      }
    }));
  };

  const addDomain = () => {
    if (newDomain && !formData.perimeter.domains.includes(newDomain)) {
      setFormData(prev => ({
        ...prev,
        perimeter: {
          ...prev.perimeter,
          domains: [...prev.perimeter.domains, newDomain]
        }
      }));
      setNewDomain('');
    }
  };

  const removeDomain = (domain) => {
    setFormData(prev => ({
      ...prev,
      perimeter: {
        ...prev.perimeter,
        domains: prev.perimeter.domains.filter(d => d !== domain)
      }
    }));
  };

  const addApplication = () => {
    if (newApp.name && newApp.url) {
      setFormData(prev => ({
        ...prev,
        perimeter: {
          ...prev.perimeter,
          applications: [...prev.perimeter.applications, { ...newApp }]
        }
      }));
      setNewApp({ name: '', url: '', description: '' });
    }
  };

  const removeApplication = (index) => {
    setFormData(prev => ({
      ...prev,
      perimeter: {
        ...prev.perimeter,
        applications: prev.perimeter.applications.filter((_, i) => i !== index)
      }
    }));
  };

  const addExclusion = () => {
    if (newExclusion && !formData.perimeter.exclusions.includes(newExclusion)) {
      setFormData(prev => ({
        ...prev,
        perimeter: {
          ...prev.perimeter,
          exclusions: [...prev.perimeter.exclusions, newExclusion]
        }
      }));
      setNewExclusion('');
    }
  };

  const removeExclusion = (exclusion) => {
    setFormData(prev => ({
      ...prev,
      perimeter: {
        ...prev.perimeter,
        exclusions: prev.perimeter.exclusions.filter(e => e !== exclusion)
      }
    }));
  };

  const toggleTestType = (type) => {
    setFormData(prev => ({
      ...prev,
      perimeter: {
        ...prev.perimeter,
        test_types: prev.perimeter.test_types.includes(type)
          ? prev.perimeter.test_types.filter(t => t !== type)
          : [...prev.perimeter.test_types, type]
      }
    }));
  };

  // Validation
  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.title) newErrors.title = 'Le titre est requis';
      if (!formData.provider_name) newErrors.provider_name = 'Le nom du prestataire est requis';
      if (!formData.provider_representative_name) newErrors.provider_representative_name = 'Le représentant est requis';
    }

    if (stepNumber === 2) {
      if (!formData.client_name) newErrors.client_name = 'Le nom du client est requis';
      if (!formData.client_representative_name) newErrors.client_representative_name = 'Le représentant est requis';
      if (!formData.client_contact_email) newErrors.client_contact_email = 'L\'email est requis';
      if (!formData.client_contact_phone) newErrors.client_contact_phone = 'Le téléphone est requis';
    }

    if (stepNumber === 3) {
      if (!formData.start_date) newErrors.start_date = 'La date de début est requise';
      if (!formData.end_date) newErrors.end_date = 'La date de fin est requise';
      if (formData.start_date && formData.end_date && new Date(formData.start_date) >= new Date(formData.end_date)) {
        newErrors.end_date = 'La date de fin doit être après la date de début';
      }
    }

    if (stepNumber === 4) {
      const perimeter = formData.perimeter;
      if (perimeter.ip_ranges.length === 0 && perimeter.domains.length === 0 && perimeter.applications.length === 0) {
        newErrors.perimeter = 'Définissez au moins une cible (IP, domaine ou application)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  // Transform frontend perimeter format to backend PerimeterItem[] format
  const transformPerimeter = (perimeterData) => {
    const items = [];
    let refCounter = 1;

    // Add IP ranges
    perimeterData.ip_ranges.forEach(ip => {
      items.push({
        ref: `P${refCounter++}`,
        type: 'IP/CIDR',
        detail: ip,
        env: 'Production',
        comment: ''
      });
    });

    // Add domains
    perimeterData.domains.forEach(domain => {
      items.push({
        ref: `P${refCounter++}`,
        type: 'Domaine',
        detail: domain,
        env: 'Production',
        comment: ''
      });
    });

    // Add applications
    perimeterData.applications.forEach(app => {
      items.push({
        ref: `P${refCounter++}`,
        type: 'Application',
        detail: app.url || app.name,
        env: 'Production',
        comment: app.description || app.name
      });
    });

    // Add exclusions as negative scope items
    perimeterData.exclusions.forEach(exclusion => {
      items.push({
        ref: `E${refCounter++}`,
        type: 'Exclusion',
        detail: exclusion,
        env: 'N/A',
        comment: 'Hors périmètre'
      });
    });

    // Add test types as metadata
    if (perimeterData.test_types && perimeterData.test_types.length > 0) {
      items.push({
        ref: `T1`,
        type: 'Types de tests',
        detail: perimeterData.test_types.join(', '),
        env: 'N/A',
        comment: ''
      });
    }

    // Add special conditions if present
    if (perimeterData.special_conditions) {
      items.push({
        ref: `C1`,
        type: 'Conditions',
        detail: perimeterData.special_conditions.substring(0, 200),
        env: 'N/A',
        comment: 'Conditions particulières'
      });
    }

    return items;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setLoading(true);
    try {
      // Transform perimeter to backend format
      const transformedPerimeter = transformPerimeter(formData.perimeter);

      const payload = {
        ...formData,
        workspace_id: workspace.id,
        template_id: formData.template_id || null,
        perimeter: transformedPerimeter
      };

      const response = await contractsApi.create(payload);
      onCreated(response);
    } catch (error) {
      console.error('Error creating contract:', error);
      const errorMessage = error.response?.data?.detail;
      // Handle case where detail is an array (validation errors)
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg || e.message || String(e)).join(', '));
      } else if (typeof errorMessage === 'object') {
        toast.error(JSON.stringify(errorMessage));
      } else {
        toast.error(errorMessage || 'Erreur lors de la création du contrat');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3, 4, 5].map((s) => (
        <React.Fragment key={s}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              s < step ? 'bg-green-600 text-white' :
              s === step ? 'bg-brand-green text-black' :
              'bg-gray-700 text-gray-400'
            }`}
          >
            {s < step ? <Check className="w-4 h-4" /> : s}
          </div>
          {s < 5 && (
            <div className={`w-8 h-0.5 ${s < step ? 'bg-green-600' : 'bg-gray-700'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Render steps
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-brand-green" />
              Informations du prestataire
              {loadingProfile && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </h3>

            <InputField label="Titre du contrat" name="title" value={formData.title} onChange={handleChange} error={errors.title} required />

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Nom de la société" name="provider_name" value={formData.provider_name} onChange={handleChange} error={errors.provider_name} required />
              <InputField label="Forme juridique" name="provider_legal_form" value={formData.provider_legal_form} onChange={handleChange} error={errors.provider_legal_form} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Capital social" name="provider_capital" value={formData.provider_capital} onChange={handleChange} error={errors.provider_capital} />
              <InputField label="SIREN" name="provider_siren" value={formData.provider_siren} onChange={handleChange} error={errors.provider_siren} />
            </div>

            <InputField label="Adresse" name="provider_address" value={formData.provider_address} onChange={handleChange} error={errors.provider_address} />

            <div className="grid grid-cols-3 gap-4">
              <InputField label="Code postal" name="provider_postal_code" value={formData.provider_postal_code} onChange={handleChange} error={errors.provider_postal_code} />
              <InputField label="Ville" name="provider_city" value={formData.provider_city} onChange={handleChange} error={errors.provider_city} />
              <InputField label="Pays" name="provider_country" value={formData.provider_country} onChange={handleChange} error={errors.provider_country} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Représentant légal" name="provider_representative_name" value={formData.provider_representative_name} onChange={handleChange} error={errors.provider_representative_name} required />
              <InputField label="Fonction" name="provider_representative_function" value={formData.provider_representative_function} onChange={handleChange} error={errors.provider_representative_function} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Contact technique" name="provider_contact_name" value={formData.provider_contact_name} onChange={handleChange} error={errors.provider_contact_name} />
              <InputField label="E-mail contact" name="provider_contact_email" value={formData.provider_contact_email} onChange={handleChange} error={errors.provider_contact_email} type="email" placeholder="contact@exemple.fr" />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <User className="w-5 h-5 text-green-400" />
                Informations du client
              </h3>
            </div>

            {/* SIREN/SIRET Lookup */}
            <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <label className="block text-sm font-medium text-blue-300 mb-2">
                Recherche automatique (SIREN, SIRET ou nom)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={sirenSearch}
                  onChange={(e) => setSirenSearch(e.target.value)}
                  placeholder="Ex: 123456789 ou Nom de l'entreprise"
                  className="flex-1 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSirenLookup())}
                />
                <button
                  type="button"
                  onClick={handleSirenLookup}
                  disabled={searchingSiren}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
                >
                  {searchingSiren ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Rechercher
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Les informations seront pre-remplies automatiquement depuis les registres officiels
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Nom de la société" name="client_name" value={formData.client_name} onChange={handleChange} error={errors.client_name} required />
              <InputField label="Forme juridique" name="client_legal_form" value={formData.client_legal_form} onChange={handleChange} error={errors.client_legal_form} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Capital social" name="client_capital" value={formData.client_capital} onChange={handleChange} error={errors.client_capital} />
              <InputField label="SIRET" name="client_siret" value={formData.client_siret} onChange={handleChange} error={errors.client_siret} />
            </div>

            <InputField label="Adresse" name="client_address" value={formData.client_address} onChange={handleChange} error={errors.client_address} />

            <div className="grid grid-cols-3 gap-4">
              <InputField label="Code postal" name="client_postal_code" value={formData.client_postal_code} onChange={handleChange} error={errors.client_postal_code} />
              <InputField label="Ville" name="client_city" value={formData.client_city} onChange={handleChange} error={errors.client_city} />
              <InputField label="Pays" name="client_country" value={formData.client_country} onChange={handleChange} error={errors.client_country} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Représentant légal" name="client_representative_name" value={formData.client_representative_name} onChange={handleChange} error={errors.client_representative_name} required />
              <InputField label="Fonction" name="client_representative_function" value={formData.client_representative_function} onChange={handleChange} error={errors.client_representative_function} />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <InputField label="Contact" name="client_contact_name" value={formData.client_contact_name} onChange={handleChange} error={errors.client_contact_name} />
              <InputField label="E-mail" name="client_contact_email" value={formData.client_contact_email} onChange={handleChange} error={errors.client_contact_email} type="email" required placeholder="contact@exemple.fr" />
              <InputField label="Téléphone" name="client_contact_phone" value={formData.client_contact_phone} onChange={handleChange} error={errors.client_contact_phone} type="tel" required placeholder="+33 6 XX XX XX XX" helperText="Format international requis : +33 6..." />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              Période d'intervention
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Date de début" name="start_date" value={formData.start_date} onChange={handleChange} error={errors.start_date} type="date" required />
              <InputField label="Date de fin" name="end_date" value={formData.end_date} onChange={handleChange} error={errors.end_date} type="date" required />
            </div>

            <InputField label="Plages horaires" name="time_slots" value={formData.time_slots} onChange={handleChange} error={errors.time_slots} placeholder="Ex: Du lundi au vendredi, de 9h00 à 18h00" />

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Période de risque après intervention (jours)
              </label>
              <input
                type="number"
                name="risk_period_days"
                value={formData.risk_period_days}
                onChange={handleChange}
                min="0"
                max="365"
                className="w-32 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white focus:outline-none focus:border-brand-green"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Livrables</label>
              <textarea
                name="deliverables_description"
                value={formData.deliverables_description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InputField label="Droit applicable" name="applicable_law" value={formData.applicable_law} onChange={handleChange} error={errors.applicable_law} />
              <InputField label="Juridiction compétente" name="jurisdiction" value={formData.jurisdiction} onChange={handleChange} error={errors.jurisdiction} />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-400" />
              Périmètre de test
            </h3>

            {errors.perimeter && (
              <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg flex items-center gap-2 text-red-400">
                <AlertCircle className="w-4 h-4" />
                {errors.perimeter}
              </div>
            )}

            {/* Test types */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Types de tests</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'external', label: 'Test externe' },
                  { id: 'internal', label: 'Test interne' },
                  { id: 'web', label: 'Application web' },
                  { id: 'mobile', label: 'Application mobile' },
                  { id: 'api', label: 'API' },
                  { id: 'wifi', label: 'WiFi' },
                  { id: 'social', label: 'Ingénierie sociale' },
                  { id: 'physical', label: 'Physique' }
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleTestType(id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      formData.perimeter.test_types.includes(id)
                        ? 'bg-brand-green text-black font-medium'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* IP Ranges */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Adresses IP / CIDR</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newIpRange}
                  onChange={(e) => setNewIpRange(e.target.value)}
                  placeholder="192.168.1.0/24 ou 10.0.0.1-10.0.0.255"
                  className="flex-1 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addIpRange())}
                />
                <button
                  type="button"
                  onClick={addIpRange}
                  className="px-3 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.perimeter.ip_ranges.map((ip) => (
                  <span key={ip} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 rounded-lg text-sm text-gray-300">
                    {ip}
                    <button onClick={() => removeIpRange(ip)} className="text-gray-500 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Domains */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Domaines</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="exemple.com ou *.exemple.com"
                  className="flex-1 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                />
                <button
                  type="button"
                  onClick={addDomain}
                  className="px-3 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.perimeter.domains.map((domain) => (
                  <span key={domain} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 rounded-lg text-sm text-gray-300">
                    <Globe className="w-3 h-3" />
                    {domain}
                    <button onClick={() => removeDomain(domain)} className="text-gray-500 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Applications */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Applications</label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <input
                  type="text"
                  value={newApp.name}
                  onChange={(e) => setNewApp(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nom de l'application"
                  className="px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                />
                <input
                  type="text"
                  value={newApp.url}
                  onChange={(e) => setNewApp(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="URL"
                  className="px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newApp.description}
                    onChange={(e) => setNewApp(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Description"
                    className="flex-1 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                  />
                  <button
                    type="button"
                    onClick={addApplication}
                    className="px-3 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {formData.perimeter.applications.length > 0 && (
                <div className="space-y-2">
                  {formData.perimeter.applications.map((app, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                      <div>
                        <span className="text-white font-medium">{app.name}</span>
                        <span className="text-gray-400 ml-2">{app.url}</span>
                        {app.description && <span className="text-gray-500 ml-2">- {app.description}</span>}
                      </div>
                      <button onClick={() => removeApplication(index)} className="text-gray-500 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Exclusions */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Exclusions (hors périmètre)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newExclusion}
                  onChange={(e) => setNewExclusion(e.target.value)}
                  placeholder="IP, domaine ou système exclu"
                  className="flex-1 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclusion())}
                />
                <button
                  type="button"
                  onClick={addExclusion}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.perimeter.exclusions.map((exclusion) => (
                  <span key={exclusion} className="inline-flex items-center gap-1 px-2 py-1 bg-red-900/30 border border-red-700 rounded-lg text-sm text-red-400">
                    {exclusion}
                    <button onClick={() => removeExclusion(exclusion)} className="text-red-500 hover:text-red-300">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Special conditions */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Conditions particulières</label>
              <textarea
                value={formData.perimeter.special_conditions}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  perimeter: { ...prev.perimeter, special_conditions: e.target.value }
                }))}
                rows={3}
                placeholder="Conditions ou restrictions supplémentaires..."
                className="w-full px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Récapitulatif
            </h3>

            <div className="bg-gray-700/50 rounded-lg p-4 space-y-4">
              {/* Contract info */}
              <div className="min-w-0">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Contrat</h4>
                <p className="text-white break-words" title={formData.title}>{formData.title}</p>
              </div>

              {/* Parties */}
              <div className="grid grid-cols-2 gap-4">
                <div className="min-w-0">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Prestataire</h4>
                  <p className="text-white truncate" title={formData.provider_name}>{formData.provider_name}</p>
                  <p className="text-gray-400 text-sm truncate" title={formData.provider_representative_name}>{formData.provider_representative_name}</p>
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Client</h4>
                  <p className="text-white truncate" title={formData.client_name}>{formData.client_name}</p>
                  <p className="text-gray-400 text-sm truncate" title={formData.client_representative_name}>{formData.client_representative_name}</p>
                  <p className="text-gray-400 text-sm truncate" title={formData.client_contact_email}>{formData.client_contact_email}</p>
                </div>
              </div>

              {/* Period */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Période d'intervention</h4>
                <p className="text-white">
                  Du {new Date(formData.start_date).toLocaleDateString('fr-FR')} au {new Date(formData.end_date).toLocaleDateString('fr-FR')}
                </p>
                <p className="text-gray-400 text-sm">{formData.time_slots}</p>
              </div>

              {/* Perimeter */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Périmètre</h4>

                {/* Test types */}
                {formData.perimeter.test_types && formData.perimeter.test_types.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.perimeter.test_types.map(type => (
                      <span key={type} className="px-2 py-1 bg-brand-green/20 text-brand-green rounded text-xs">
                        {type}
                      </span>
                    ))}
                  </div>
                )}

                {/* IP Ranges */}
                {formData.perimeter.ip_ranges && formData.perimeter.ip_ranges.length > 0 && (
                  <div className="mt-2">
                    <p className="text-gray-400 text-sm font-medium">Plages IP ({formData.perimeter.ip_ranges.length}):</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.perimeter.ip_ranges.map((ip, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-900/30 text-blue-400 rounded text-xs">
                          {ip}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Domains */}
                {formData.perimeter.domains && formData.perimeter.domains.length > 0 && (
                  <div className="mt-2">
                    <p className="text-gray-400 text-sm font-medium">Domaines ({formData.perimeter.domains.length}):</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.perimeter.domains.map((domain, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-purple-900/30 text-purple-400 rounded text-xs">
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Applications */}
                {formData.perimeter.applications && formData.perimeter.applications.length > 0 && (
                  <div className="mt-2">
                    <p className="text-gray-400 text-sm font-medium">Applications ({formData.perimeter.applications.length}):</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.perimeter.applications.map((app, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-orange-900/30 text-orange-400 rounded text-xs">
                          {app.name} - {app.url}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Exclusions */}
                {formData.perimeter.exclusions && formData.perimeter.exclusions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-gray-400 text-sm font-medium">Exclusions ({formData.perimeter.exclusions.length}):</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.perimeter.exclusions.map((ex, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-900/30 text-red-400 rounded text-xs">
                          {ex}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {(!formData.perimeter.ip_ranges || formData.perimeter.ip_ranges.length === 0) &&
                 (!formData.perimeter.domains || formData.perimeter.domains.length === 0) &&
                 (!formData.perimeter.applications || formData.perimeter.applications.length === 0) && (
                  <p className="text-yellow-400 text-sm italic">Aucune cible définie</p>
                )}
              </div>
            </div>

            <div className="p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
              <p className="text-yellow-400 text-sm">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                En créant ce contrat, vous pourrez l'envoyer au client pour signature électronique.
                Le client recevra un email avec un lien sécurisé pour consulter et signer le document.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-green/20 rounded-lg">
              <FileSignature className="w-6 h-6 text-brand-green" />
            </div>
            <h2 className="text-lg font-semibold text-white">Nouveau contrat d'autorisation</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-4 pt-4">
          <StepIndicator />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-700">
          <button
            onClick={prevStep}
            disabled={step === 1}
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Précédent
          </button>

          {step < 5 ? (
            <button
              onClick={nextStep}
              className="flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg font-medium transition-colors"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg font-medium disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Créer le contrat
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateContractDialog;
